import { useAddBusyState } from '#hooks/PRIVATE_useAddBusyState';
import { UnsafeDomHandle, UNSAFE_DOM_ACCESS } from '#utils/UNSAFE_unsafeDomAccess';
import type { RefObject } from 'preact';
import { useLayoutEffect } from 'preact/hooks';

type Options = {
  /**
   * When set to true, the callback will not be invoked even if the target element's size changes.
   */
  isDisabled?: boolean;

  /**
   * Sets which box model the observer will observe changes to.
   */
  box?: ResizeObserverBoxOptions;
};

/**
 * A hook for observing and responding to changes to the size of a DOM element.
 * It utilizes a ResizeObserver to observe changes to the size of the specified element
 * and accepts a callback function to respond to these changes.
 *
 * @param ref The ref to the target DOM element to observe.
 *     Note that if the element is dynamically added or remounted (e.g., due to conditional rendering or changes in dynamic lists),
 *     a simple useRef object will not automatically trigger the hook to re-observe the new element.
 *     To handle such cases, ensure that the hook receives a new ref whenever the DOM element changes.
 *     One way to achieve this is by storing the ref in state and updating it using a callback ref.
 * @param callback A callback function to be called when the size of the observed element changes.
 *     If the provided callback triggers the consuming component to re-render, then
 *     it's important to pass the same instance of the callback function during each render
 *     to prevent infinite re-render loops. One way to achieve this is by using useCallback.
 * @param options Configuration options.
 */
const useResizeObserver = (
  ref: RefObject<Element> | RefObject<UnsafeDomHandle<Element>>,
  callback: (entry: ResizeObserverEntry) => void,
  options?: Options
) => {
  const { box, isDisabled = false } = options || {};
  const { onBusyStart, onBusyEnd } = useAddBusyState('useResizeObserver: resizing');

  useLayoutEffect(() => {
    // Note ref may be undefined in the hooks storybook demo
    if (!ref?.current || isDisabled) return;

    const target = ref.current instanceof Element ? ref.current : ref.current[UNSAFE_DOM_ACCESS];
    if (!target) return;

    onBusyStart();
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        callback(entry);
      }
      onBusyEnd();
    });
    resizeObserver.observe(target, { box });

    return () => {
      resizeObserver.disconnect();
      onBusyEnd();
    };
  }, [ref, callback, box, isDisabled, onBusyStart, onBusyEnd]);
};

export { useResizeObserver };
